// src/utils/customNav.js
//
// ORG-DEFINED NAV SECTIONS. A client can add its own content area to the
// sidebar — Hockey Alberta's "Hockey Culture -> Respect & Belonging" is the
// first — without a code change and without touching any other org.
//
// Shape, on the org doc:
//   organizations/{id}.customNav = [
//     { id: 'custom-hockey-culture', label: 'Hockey Culture', icon: 'Trophy',
//       subItems: [{ id: 'custom-respect-belonging', label: 'Respect & Belonging' }] }
//   ]
//   organizations/{id}.customNavLabel = 'Hockey Alberta'   // sidebar group heading
//
// A section is one of two KINDS:
//   'section' (default) - a heading with topics under it. Each topic is a page.
//   'landing'           - the entry IS the page: a single designed landing page
//                         (hero, a statement, commitments, steps, actions) with no
//                         topics under it. Content lives in the same place as a
//                         topic's, under a `landing` map:
//     organizations/{id}/private_topics/{section.id}.landing = { eyebrow, headline,
//       tagline, intro, statement, commitments[{keyword,text}], steps[{title,text}],
//       actions[{label,target,primary}] }   // target: a tab id or an https url
//
// Each sub-item IS a topic. Its page is the standard TopicView, fed from
//   organizations/{id}/private_topics/{subItem.id}   -> { title, description, modules }
// which the Content Library already writes to (scope: this organization). So a
// custom section inherits modules, videos, PDFs, the player, everything — and
// the content is private to the org by the existing Firestore rules.
//
// IDS ARE KEYS. They become URL hashes and Firestore doc ids, so they are
// generated once from the label and never regenerated when the label changes
// ("rename labels, never keys"). The `custom-` prefix makes a collision with a
// built-in tab id impossible — an org calling a section "Sleep" must not be
// able to shadow the real Sleep topic.

export const CUSTOM_PREFIX = 'custom-';

export const isCustomId = (id) => typeof id === 'string' && id.startsWith(CUSTOM_PREFIX);

export const slugify = (label) =>
    String(label || '')
        .toLowerCase()
        .replace(/&/g, ' and ')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 48);

// A fresh id for a label, unique within `taken`.
export function makeCustomId(label, taken = []) {
    const base = CUSTOM_PREFIX + (slugify(label) || 'section');
    if (!taken.includes(base)) return base;
    let n = 2;
    while (taken.includes(`${base}-${n}`)) n += 1;
    return `${base}-${n}`;
}

// Icon names an org may pick. Sidebar maps these to lucide components; an
// unknown name falls back to the first.
export const CUSTOM_NAV_ICONS = ['Trophy', 'Award', 'Flag', 'ShieldCheck', 'Users', 'HeartHandshake', 'Star', 'BookOpen', 'Megaphone'];

// Normalised, defensive read of an org's custom sections. Drops anything
// malformed rather than letting one bad entry break the sidebar.
export function customSections(org) {
    const raw = Array.isArray(org?.customNav) ? org.customNav : [];
    return raw
        .filter((s) => s && isCustomId(s.id) && (s.label || '').trim())
        .map((s) => {
            const kind = s.kind === 'landing' ? 'landing' : 'section';
            return {
                id: s.id,
                kind,
                label: s.label.trim(),
                icon: CUSTOM_NAV_ICONS.includes(s.icon) ? s.icon : CUSTOM_NAV_ICONS[0],
                description: (s.description || '').trim(),
                // A landing page has no topics under it, whatever the data says.
                subItems: kind === 'landing' ? [] : (Array.isArray(s.subItems) ? s.subItems : [])
                    .filter((t) => t && isCustomId(t.id) && (t.label || '').trim())
                    .map((t) => ({ id: t.id, label: t.label.trim(), description: (t.description || '').trim() }))
            };
        })
        // A plain section with no topics is a dead end; a landing page is itself the page.
        .filter((s) => s.kind === 'landing' || s.subItems.length > 0);
}

export const isLandingSection = (section) => !!section && section.kind === 'landing';

// Every custom page an org has, topic or landing, as { id, title } - what the
// Content Library offers as a destination for that org's resources.
export const customPages = (org) => customSections(org).flatMap((s) =>
    (isLandingSection(s)
        ? [{ id: s.id, title: s.label }]
        : s.subItems.map((t) => ({ id: t.id, title: `${s.label} - ${t.label}` }))));

// "KEYWORD | text" lines <-> [{keyword, text}]. Same textarea convention as the
// EAP services field: these get pasted from a client's one-pager in a batch.
export const parsePairs = (text, a, b) => String(text || '').split('\n')
    .map((line) => { const [x, ...rest] = line.split('|'); return { [a]: (x || '').trim(), [b]: rest.join('|').trim() }; })
    .filter((o) => o[a] || o[b]);
export const serializePairs = (list, a, b) => (Array.isArray(list) ? list : [])
    .map((o) => [o[a], o[b]].filter(Boolean).join(' | ')).join('\n');

export const customTopicIds = (org) => customSections(org).flatMap((s) => s.subItems.map((t) => t.id));

export const findCustomSection = (org, id) => customSections(org).find((s) => s.id === id) || null;

export const customGroupLabel = (org) =>
    (org?.customNavLabel || '').trim() || (org?.branding?.hubName || '').trim() || org?.name || 'Your Organization';
